/**
 * reportTemplates.js — renders the synthetic model (reportEngine.js) into a
 * self-contained, premium TeamGrid-branded HTML report string.
 *
 * Design system is ported verbatim from the real OrgReports pipeline
 * (_css.txt) so the demo output is pixel-consistent with genuine reports.
 * No external JS/images at render time except the inlined TeamGrid logo.
 *
 * Templates registry at the bottom — CEO Brief today; Weekly / Practice /
 * Work Log slot in as additional render fns later.
 */
import { INR, titleCase, initials, TIERS } from './reportEngine';

const esc = (s) =>
  String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ─── Design system (ported from OrgReports/_css.txt) ─────────────────────────
export const REPORT_CSS = `
*{box-sizing:border-box;margin:0;padding:0}
:root{--gold:#c5920a;--gold2:#e0a82e;--red:#d64550;--amber:#e8912d;--green:#1f9d6b;--teal:#0d6e6e;--blue:#2563eb;--navy:#0f1b2d;--navy2:#16263d;--ink:#1a2230;--sub:#5a6675;--line:#e7ebf0;--bg:#f6f8fb;--card:#ffffff}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-family:'Rubik',-apple-system,Segoe UI,Roboto,sans-serif;font-size:15.5px;line-height:1.68;color:var(--ink);background:var(--bg);-webkit-font-smoothing:antialiased;overflow-x:hidden}
.wrap{max-width:1060px;margin:0 auto;padding:0 46px}.mono{font-variant-numeric:tabular-nums}.muted{color:#8b97a6}
section{padding:64px 0}.alt{background:#fff}
.eyebrow{font-family:ui-monospace,Menlo,monospace;font-size:11.5px;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);font-weight:600;margin-bottom:12px;display:flex;align-items:center;gap:10px}
.eyebrow .no{color:#c3ccd6;font-weight:700}
h2.sec{font-size:32px;font-weight:700;letter-spacing:-.028em;line-height:1.12;margin-bottom:8px}
.sub{color:var(--sub);font-size:16px;margin-bottom:34px;font-weight:300;max-width:74ch}
.card{background:var(--card);border:1px solid var(--line);border-radius:16px;box-shadow:0 1px 2px rgba(15,27,45,.04),0 6px 22px rgba(15,27,45,.04);padding:28px}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:24px}.grid3{display:grid;grid-template-columns:repeat(3,1fr);gap:22px}
.cover{min-height:97vh;background:var(--navy);color:#fff;display:flex;flex-direction:column;justify-content:center;position:relative;overflow:hidden;background-image:radial-gradient(1000px 520px at 84% 4%,rgba(197,146,10,.22),transparent),radial-gradient(760px 460px at 2% 96%,rgba(13,110,110,.22),transparent)}
.logo{display:flex;align-items:center;gap:12px;margin-bottom:40px}
.logoimg{height:30px;width:auto;display:block}
.logo .ls{font-family:ui-monospace,Menlo,monospace;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#9aa6b5;margin-left:2px}
.cover h1{font-size:58px;font-weight:800;line-height:1.06;letter-spacing:-.032em;max-width:17ch}.cover h1 .g{color:var(--gold)}
.cover .desc{font-size:19px;font-weight:300;color:#c7cfda;margin-top:22px;max-width:58ch}
.cover .period{font-family:ui-monospace,Menlo,monospace;font-size:13px;letter-spacing:.06em;color:#9aa6b5;margin-top:26px}
.statusband{display:inline-flex;align-items:center;gap:11px;margin-top:26px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.13);padding:11px 18px;border-radius:40px;font-size:14.5px;color:#e7ecf2}
.statusband .dot{width:10px;height:10px;border-radius:50%}
.strip{display:flex;gap:0;margin-top:52px;flex-wrap:wrap;border-top:1px solid rgba(255,255,255,.13);padding-top:30px}
.strip .cell{flex:1;min-width:150px;padding-right:26px}
.strip .k{font-family:ui-monospace,Menlo,monospace;font-size:10.5px;letter-spacing:.13em;text-transform:uppercase;color:#8b97a6}
.strip .v{font-size:32px;font-weight:700;margin-top:6px}.strip .v small{color:var(--gold);font-size:15px;font-weight:600}
.verdict{display:flex;gap:18px;align-items:flex-start;background:#fff;border:1px solid var(--line);border-left:5px solid var(--amber);border-radius:14px;padding:24px 26px}
.verdict .badge{flex:0 0 auto;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#fff;padding:6px 14px;border-radius:30px}
.verdict .vt{font-size:19px;font-weight:600;line-height:1.4}
.kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:16px;margin-top:26px}
.kpi{background:#fff;border:1px solid var(--line);border-radius:14px;padding:20px}
.kpi .l{font-family:ui-monospace,Menlo,monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:#8b97a6}
.kpi .n{font-size:29px;font-weight:700;margin-top:8px;letter-spacing:-.02em}.kpi .s{font-size:12.5px;margin-top:5px;font-weight:500}
.sig{display:flex;gap:22px;padding:2px 0}.sigcard{margin-bottom:16px;padding:24px 28px}
.sig .tag{flex:0 0 118px}.sig .tag span{display:inline-block;font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#fff;padding:6px 13px;border-radius:30px}
.sig h3{font-size:22px;font-weight:700;letter-spacing:-.018em;line-height:1.24}
.sig .sd{font-size:15px;color:#48525f;font-weight:300;margin-top:9px}
.sig .so{font-size:14px;font-weight:500;margin-top:14px;padding:12px 16px;background:#f4f7fb;border-left:3px solid var(--navy);border-radius:8px}.sig .so b{color:var(--navy)}
.risk{display:grid;grid-template-columns:120px 1fr;gap:0;border:1px solid var(--line);border-radius:14px;overflow:hidden;margin-bottom:16px;background:#fff}
.risk .rl{padding:22px 20px;display:flex;flex-direction:column;gap:10px;border-right:1px solid var(--line)}
.risk .sev{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#fff;padding:5px 11px;border-radius:30px;text-align:center}
.risk .rsig{font-size:11.5px;color:var(--sub)}
.risk .rr{padding:22px 24px}
.risk h4{font-size:18px;font-weight:600;margin-bottom:7px}
.risk .rd{font-size:14.5px;color:#48525f;font-weight:300}
.risk .mv{font-size:13.5px;margin-top:12px;padding:11px 15px;background:#f4f7fb;border-radius:8px}.risk .mv b{color:var(--navy)}
.ribbon{display:flex;height:34px;border-radius:9px;overflow:hidden;margin:4px 0 14px;border:1px solid var(--line)}
.ribbon i{display:block;height:100%}
.leg{display:flex;flex-wrap:wrap;gap:8px 18px}.leg span{font-size:12.5px;color:#48525f;display:inline-flex;align-items:center;gap:7px}.leg span i{width:11px;height:11px;border-radius:3px}
.days{display:flex;align-items:flex-end;gap:10px;height:120px;margin-top:6px}
.days .day{flex:1;display:flex;flex-direction:column;align-items:center;gap:7px;height:100%;justify-content:flex-end}
.days .day .bar2{width:100%;max-width:46px;border-radius:6px 6px 0 0;background:linear-gradient(180deg,var(--gold2),var(--gold))}
.days .day .wd{font-size:11px;color:#8b97a6;font-weight:500}.days .day.we .bar2{background:#cfd6de}
.bar{display:grid;grid-template-columns:190px 1fr 52px;align-items:center;gap:14px;padding:8px 0;border-bottom:1px solid #f1f3f5}.bar:last-child{border-bottom:0}
.bar .nm{font-size:14px;font-weight:500}
.track{height:10px;background:#eef1f4;border-radius:6px;overflow:hidden}.track>i{display:block;height:100%;border-radius:6px}
.bar .v{text-align:right;font-weight:600;font-size:13px}
.keyins{margin-top:22px;padding:16px 20px;background:#fbf6e9;border-left:3px solid var(--gold);border-radius:8px;font-size:14.5px}.keyins b{color:#5a4b1f}
.fcard{background:#fff;border:1px solid var(--line);border-radius:14px;padding:22px;position:relative}
.fcard .ft{display:flex;align-items:center;justify-content:space-between;gap:10px}
.fcard h4{font-size:17px;font-weight:600}
.fstatus{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#fff;padding:4px 11px;border-radius:30px}
.fmeta{display:flex;gap:26px;margin:16px 0 12px}.fmeta .n{font-size:22px;font-weight:700}.fmeta .l{font-size:11px;color:#8b97a6}
.fwork{font-size:13px;color:#48525f}.fwork b{color:var(--ink)}
.ai{background:linear-gradient(135deg,#0d6e6e,#0a5252);color:#fff;border-radius:18px;padding:34px 38px}
.ai .row{display:flex;gap:40px;flex-wrap:wrap;margin-top:22px}
.ai .stat .n{font-size:34px;font-weight:800}.ai .stat .l{font-size:12.5px;color:#bfe3e3}
.ai p{font-size:15.5px;color:#e6f2f2;font-weight:300;max-width:70ch}
.prow{display:flex;align-items:center;gap:14px;padding:12px 0;border-bottom:1px solid #f1f3f5}.prow:last-child{border-bottom:0}
.prow .av{width:38px;height:38px;border-radius:50%;flex:0 0 38px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;background:linear-gradient(135deg,var(--gold),#1971c2)}
.prow .pn{font-weight:600;font-size:15px}.prow .pr{font-size:12px;color:var(--sub)}
.prow .ph{margin-left:auto;text-align:right}.prow .ph .n{font-weight:700;font-size:16px}.prow .ph .l{font-size:11px;color:#8b97a6}
.acard{background:#fff;border:1px solid var(--line);border-radius:14px;padding:22px}
.acard .at{font-weight:600;font-size:15.5px;display:flex;justify-content:space-between;align-items:baseline;gap:10px}.acard .pct{font-size:26px;font-weight:800;color:var(--green)}
.aprog{height:8px;background:#e6f4ee;border-radius:6px;overflow:hidden;margin:12px 0 13px}.aprog>i{display:block;height:100%;background:linear-gradient(90deg,#1f9d6b,#2bc07f);border-radius:6px}
.acard .ad{font-size:13px;color:#48525f;font-weight:300;line-height:1.55}.acard .af{margin-top:12px;font-size:12.5px;color:var(--teal);font-weight:600}
.asum{display:flex;gap:44px;flex-wrap:wrap;margin-top:26px;background:var(--navy);color:#fff;border-radius:14px;padding:26px 34px}
.asum .k{font-family:ui-monospace,Menlo,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#8b97a6}.asum .v{font-size:30px;font-weight:700;color:var(--gold);margin-top:5px}
.dark{background:var(--navy);color:#fff}
.act{display:flex;align-items:flex-start;gap:24px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:14px;padding:22px 26px;margin-bottom:14px}
.act .num{font-size:30px;font-weight:800;color:var(--gold);min-width:46px}.act .t{font-weight:600;font-size:17px;color:#fff}.act .d{font-size:14px;color:#aeb8c4;font-weight:300;margin-top:3px}.act .imp{margin-left:auto;text-align:right;font-weight:700;color:var(--gold);font-size:14px;min-width:130px}
.foot{padding:40px 0 66px;color:#8b97a6;font-size:12px;font-weight:300;line-height:1.75}
.demo{display:inline-block;font-family:ui-monospace,Menlo,monospace;font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:#c5920a;border:1px solid rgba(197,146,10,.4);padding:5px 12px;border-radius:30px;margin-bottom:22px}
/* org structure */
.ocols{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:18px;align-items:start}
.ocol{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px 16px 8px;box-shadow:0 1px 2px rgba(15,27,45,.04),0 6px 22px rgba(15,27,45,.03)}
.ohd{font-size:14px;font-weight:700;display:flex;align-items:center;justify-content:space-between;padding-bottom:12px;margin-bottom:10px;border-bottom:1px solid var(--line)}
.ohd span{font-size:11px;font-weight:600;color:#8b97a6;background:#f4f7fb;border-radius:20px;padding:2px 9px}
.otier{margin-bottom:12px}
.otl{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;margin-bottom:6px}
.orow{display:flex;align-items:center;gap:10px;padding:5px 0}
.oav{width:30px;height:30px;border-radius:50%;flex:0 0 30px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff}
.on{display:flex;flex-direction:column;line-height:1.25;min-width:0}.on b{font-size:13px;font-weight:600}.on i{font-size:11.5px;color:var(--sub);font-style:normal}
/* ── Responsive: tablet ── */
@media(max-width:900px){
  .wrap{padding:0 30px}
  .grid3{grid-template-columns:1fr 1fr}
  .kpis{grid-template-columns:repeat(3,1fr)}
  .cover h1{font-size:46px}
  section{padding:48px 0}
}
/* ── Responsive: phone ── */
@media(max-width:640px){
  .wrap{padding:0 20px}
  body{font-size:14.5px}
  .grid2,.grid3,.kpis{grid-template-columns:1fr}
  .cover{min-height:auto;padding:56px 0 48px}
  .cover h1{font-size:30px;max-width:100%}.cover .desc{font-size:16px}
  h2.sec{font-size:24px}
  .sig h3,.risk h4,.act .t{overflow-wrap:anywhere}
  .strip .cell{min-width:46%}
  .sig{flex-direction:column;gap:12px}.sig .tag{flex:0 0 auto}
  .risk{grid-template-columns:1fr}.risk .rl{flex-direction:row;align-items:center;border-right:0;border-bottom:1px solid var(--line);gap:12px}
  .bar{grid-template-columns:120px 1fr 46px}
  .act{flex-wrap:wrap}.act .imp{margin-left:0;text-align:left;min-width:auto}
  .ai .row{gap:24px}
  .asum{gap:24px}
}
/* ── Print / PDF ── */
@media print{
  /* margin:0 makes Chrome/Safari hide their default header (date/title) & footer (URL/page#). */
  @page{margin:0}
  html,body{background:#fff}
  body{font-size:11.5px}
  /* Re-introduce page breathing room as padding on the content instead of @page margin. */
  .wrap{max-width:100%;padding:0 14mm}
  /* Cover fills the whole first page (edge-to-edge navy), then breaks. */
  .cover{min-height:100vh;padding:0;border-radius:0;justify-content:center;break-after:page;break-inside:avoid}
  .cover .wrap{padding:0 14mm}
  section{padding:16mm 0}
  section:first-of-type{padding-top:0}
  /* Keep a section's heading group with the content that follows it — no orphan headings */
  .eyebrow,h2.sec,.sub{break-after:avoid}
  /* Small self-contained blocks never split */
  .kpi,.sig,.sigcard,.risk,.fcard,.acard,.act,.keyins,.verdict,.ai,.hl,.op .t,.pcard,.ocol{break-inside:avoid}
  .bar,.days,.ribbon,.leg,.prow,.mover,.orow,.opact{break-inside:avoid}
  /* Cards that contain charts or dense visuals must never be split across pages */
  .card{break-inside:avoid}
  /* Grid and list containers can split between items, but not mid-item */
  .ocols,.wk,.grid2,.grid3{break-inside:auto}
  /* Dark summary strip and automation total should not split */
  .asum{break-inside:avoid}
  .ocols{grid-template-columns:repeat(2,1fr)!important}
  .grid2{grid-template-columns:1fr 1fr!important}
  .grid3{grid-template-columns:repeat(3,1fr)!important}
  .kpis{grid-template-columns:repeat(5,1fr)!important}
  /* trim first/last vertical whitespace so pages aren't padded out */
  section > .wrap > :last-child{margin-bottom:0}
  .foot{padding:14mm 0}
  .foot,.foot .wrap{break-inside:avoid}
  .dark,.cover,.ai,.asum,.retn,.strip .v small,.kpi .n,.days .day .bar2,.tl .c .b,.ribbon i,.track > i,.aprog > i,.oav{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}`;

// palette for work-mix ribbon
const WORK_COLORS = ['#c5920a', '#0d6e6e', '#2563eb', '#1f9d6b', '#e8912d', '#8b5cf6', '#d64550', '#64748b'];

// ─── Shared building blocks (reused across templates) ────────────────────────
const logoBlock = (logoSvg) =>
  logoSvg
    ? `<div class="logo">${logoSvg}</div>`
    : `<div class="logo"><span class="logoimg" style="font-size:19px;font-weight:700;color:#fff">TeamGrid</span></div>`;

// Full-page HTML shell. extraCss lets a template add its own rules.
const docShell = (m, title, bodyHtml, extraCss = '') => `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(m.company)} — ${esc(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
<style>${REPORT_CSS}${extraCss}</style></head><body>
${bodyHtml}
</body></html>`;

// Footer disclaimer (shared). compact=true for one-pagers.
const footerBlock = (compact = false) => `
<section class="foot"><div class="wrap">
  <span class="demo">Illustrative sample · generated by TeamGrid</span><br>
  ${compact
    ? 'Demonstration report. Figures are illustrative; in a live TeamGrid deployment every metric is derived from real, consented activity data. No raw screen content is ever shown.'
    : `This is a demonstration report showing the format and depth of insight TeamGrid produces from real workforce activity.
  Figures shown are illustrative. In a live TeamGrid deployment, every metric is derived from real, consented activity data,
  daily hours are capped at 16h, and automation figures are reasoned estimates labelled as recoverable — never measured guarantees.
  No raw screen content is ever shown.`}
  &nbsp;·&nbsp; TeamGrid — Workforce Intelligence, Automated.
</div></section>`;

const workRibbonHtml = (m) => m.workMix.map((w, i) => `<i style="width:${w[1]}%;background:${WORK_COLORS[i % WORK_COLORS.length]}"></i>`).join('');
const workLegHtml = (m) => m.workMix.map((w, i) => `<span><i style="background:${WORK_COLORS[i % WORK_COLORS.length]}"></i>${esc(w[0])} · ${w[1]}%</span>`).join('');
const dayBarsHtml = (m) => {
  const maxDay = Math.max(...m.dayTrend.map((d) => d.h), 1);
  return m.dayTrend.map((d) => `<div class="day ${d.we ? 'we' : ''}"><div class="bar2" style="height:${Math.max(3, Math.round(d.h / maxDay * 100))}%"></div><div class="wd">${d.wd}</div></div>`).join('');
};

// Tier accent colours (leadership → team)
const TIER_COL = { 1: '#c5920a', 2: '#0d6e6e', 3: '#2563eb', 4: '#64748b' };

// Organisation structure section — departments as columns, people grouped by
// seniority tier. Shows the industry-standard hierarchy to the prospect.
const orgStructureSection = (m, no, alt = false) => {
  const cols = m.departments.map((d) => {
    const dp = m.people.filter((p) => p.dept === d.name).slice().sort((a, b) => (a.tier || 4) - (b.tier || 4));
    const tiers = [1, 2, 3, 4].map((tier) => {
      const inTier = dp.filter((p) => (p.tier || 4) === tier);
      if (!inTier.length) return '';
      const rows = inTier.map((p) => `
        <div class="orow"><span class="oav" style="background:${TIER_COL[tier]}">${esc(initials(p.name))}</span>
          <span class="on"><b>${esc(titleCase(p.name))}</b><i>${esc(p.role || '')}</i></span></div>`).join('');
      return `<div class="otier"><div class="otl" style="color:${TIER_COL[tier]}">${esc(TIERS[tier].label)}</div>${rows}</div>`;
    }).join('');
    return `<div class="ocol"><div class="ohd">${esc(d.name)}<span>${dp.length}</span></div>${tiers}</div>`;
  }).join('');
  const legend = [1, 2, 3, 4].map((tr) => `<span><i style="background:${TIER_COL[tr]}"></i>${esc(TIERS[tr].label)}</span>`).join('');
  return `
<section class="${alt ? 'alt' : ''}"><div class="wrap">
  <div class="eyebrow"><span class="no">${no}</span> Organisation structure</div>
  <h2 class="sec">How the ${esc(m.lingo.team)} is organised</h2>
  <p class="sub">${m.kpis.people} people across ${m.departments.length} ${m.departments.length === 1 ? 'department' : 'departments'}, by seniority.</p>
  <div class="leg" style="margin-bottom:18px">${legend}</div>
  <div class="ocols">${cols}</div>
</div></section>`;
};

// ─── CEO Brief renderer ──────────────────────────────────────────────────────
function renderCEOBrief(m, logoSvg) {
  const logo = logoSvg
    ? `<div class="logo">${logoSvg}</div>`
    : `<div class="logo"><span class="logoimg" style="font-size:19px;font-weight:700;color:#fff">TeamGrid</span></div>`;

  const workRibbon = m.workMix.map((w, i) => `<i style="width:${w[1]}%;background:${WORK_COLORS[i % WORK_COLORS.length]}"></i>`).join('');
  const workLeg = m.workMix.map((w, i) => `<span><i style="background:${WORK_COLORS[i % WORK_COLORS.length]}"></i>${esc(w[0])} · ${w[1]}%</span>`).join('');

  const maxTool = Math.max(...m.toolMix.map((t) => t[1]), 1);
  const toolBars = m.toolMix.map((t) => `
    <div class="bar"><div class="nm">${esc(t[0])}</div>
      <div class="track"><i style="width:${Math.round(t[1] / maxTool * 100)}%;background:linear-gradient(90deg,#2563eb,#60a5fa)"></i></div>
      <div class="v">${t[1]} ${t[1] === 1 ? 'person' : 'people'}</div></div>`).join('');

  const maxDay = Math.max(...m.dayTrend.map((d) => d.h), 1);
  const dayBars = m.dayTrend.map((d) => `
    <div class="day ${d.we ? 'we' : ''}"><div class="bar2" style="height:${Math.max(3, Math.round(d.h / maxDay * 100))}%"></div><div class="wd">${d.wd}</div></div>`).join('');

  const signals = m.signals.map((s) => `
    <div class="card sigcard"><div class="sig">
      <div class="tag"><span style="background:${s.col}">${esc(s.tag)}</span></div>
      <div><h3>${esc(s.title)}</h3><div class="sd">${esc(s.desc)}</div>
        <div class="so"><b>→ Decision:</b> ${esc(s.decision)}</div></div>
    </div></div>`).join('');

  const deptCards = m.departments.map((d) => `
    <div class="fcard"><div class="ft"><h4>${esc(d.name)}</h4><span class="fstatus" style="background:${d.col}">${esc(d.status)}</span></div>
      <div class="fmeta"><div><div class="n">${d.count}</div><div class="l">People</div></div>
        <div><div class="n">${d.hours.toLocaleString('en-IN')}</div><div class="l">Hours</div></div>
        <div><div class="n">${d.focus}%</div><div class="l">Focus</div></div>
        <div><div class="n">${d.share}%</div><div class="l">Of output</div></div></div>
      ${d.top ? `<div class="fwork">Top contributor: <b>${esc(titleCase(d.top.name))}</b> · ${d.top.hours} h</div>` : ''}
    </div>`).join('');

  const topContrib = m.people.slice(0, 8).map((p) => `
    <div class="prow"><div class="av">${esc(initials(p.name))}</div>
      <div><div class="pn">${esc(titleCase(p.name))}</div><div class="pr">${esc(p.role || p.dept || '')}${p.usesAI ? ' · AI-assisted' : ''}</div></div>
      <div class="ph"><div class="n">${p.hours} h</div><div class="l">${p.focus}% focus</div></div></div>`).join('');

  const risks = m.risks.map((r) => `
    <div class="risk"><div class="rl"><span class="sev" style="background:${r.col}">${esc(r.sev)}</span><span class="rsig">${esc(r.signal)}</span></div>
      <div class="rr"><h4>${esc(r.t)}</h4><div class="rd">${esc(r.d)}</div><div class="mv"><b>Move:</b> ${esc(r.move)}</div></div></div>`).join('');

  const autos = m.automations.map((a) => `
    <div class="acard"><div class="at"><span>${esc(a.task)}</span><span class="pct">${a.pct}%</span></div>
      <div class="aprog"><i style="width:${a.pct}%"></i></div>
      <div class="ad">${esc(a.today)} ${esc(a.replace)}</div>
      <div class="af">≈ ${INR(a.save)}/yr recoverable (est.)</div></div>`).join('');

  const actions = m.actions.map((a, i) => `
    <div class="act"><div class="num">${String(i + 1).padStart(2, '0')}</div>
      <div><div class="t">${esc(a.t)}</div><div class="d">${esc(a.d)}</div></div>
      <div class="imp">${esc(a.imp)}</div></div>`).join('');

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(m.company)} — Workforce Intelligence Report</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
<style>${REPORT_CSS}</style></head><body>

<section class="cover"><div class="wrap">
  ${logo}
  <h1>${esc(m.company)} <span class="g">Workforce Intelligence</span> Report</h1>
  <div class="desc">A founder-grade read of how the ${esc(m.lingo.team)} actually spent its time — where the leverage is, what's at risk, and what to do next.</div>
  <div class="statusband"><span class="dot" style="background:${m.verdict.col}"></span>Overall: <b style="color:#fff">&nbsp;${esc(m.verdict.label)}</b></div>
  <div class="period">${esc(m.industry)} · ${esc(m.period)} · ${m.workingDays} working days</div>
  <div class="strip">
    <div class="cell"><div class="k">People tracked</div><div class="v">${m.kpis.people}</div></div>
    <div class="cell"><div class="k">Hours of work</div><div class="v">${m.kpis.hours.toLocaleString('en-IN')}</div></div>
    <div class="cell"><div class="k">Active focus</div><div class="v">${m.kpis.focus}<small>%</small></div></div>
    <div class="cell"><div class="k">Using AI daily</div><div class="v">${m.aiUsers}<small>/${m.kpis.people}</small></div></div>
  </div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">01</span> Executive read</div>
  <h2 class="sec">The headline</h2>
  <p class="sub">What a founder needs to know before the detail.</p>
  <div class="verdict"><span class="badge" style="background:${m.verdict.col}">${esc(m.verdict.label)}</span>
    <div class="vt">${esc(m.verdict.text)}</div></div>
  <div class="kpis">
    <div class="kpi"><div class="l">People</div><div class="n">${m.kpis.people}</div><div class="s" style="color:var(--sub)">tracked this period</div></div>
    <div class="kpi"><div class="l">Hours</div><div class="n">${m.kpis.hours.toLocaleString('en-IN')}</div><div class="s" style="color:var(--sub)">of tracked work</div></div>
    <div class="kpi"><div class="l">Focus</div><div class="n">${m.kpis.focus}%</div><div class="s" style="color:${m.kpis.focus >= 78 ? 'var(--green)' : 'var(--amber)'}">active on-task</div></div>
    <div class="kpi"><div class="l">AI adoption</div><div class="n">${m.aiUsers}</div><div class="s" style="color:var(--teal)">of ${m.kpis.people} people</div></div>
    <div class="kpi"><div class="l">Concentration</div><div class="n">${m.top2Share}%</div><div class="s" style="color:var(--amber)">from top 2</div></div>
  </div>
</div></section>

<section><div class="wrap">
  <div class="eyebrow"><span class="no">02</span> What matters this week</div>
  <h2 class="sec">Three things that matter</h2>
  <p class="sub">Each one ends in a decision you can make today.</p>
  ${signals}
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">03</span> The work, in two charts</div>
  <h2 class="sec">Where the time went</h2>
  <div class="grid2">
    <div class="card"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Work by type</h4>
      <div class="ribbon">${workRibbon}</div><div class="leg">${workLeg}</div>
      <div class="keyins"><b>So what:</b> ${esc(m.workMix[0][0])} is the engine of the team's output at ${m.workMix[0][1]}% of hours.</div></div>
    <div class="card"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Tool adoption</h4>${toolBars}</div>
  </div>
  <div class="card" style="margin-top:24px"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Daily activity through the period</h4>
    <div class="days">${dayBars}</div></div>
</div></section>

${orgStructureSection(m, '04', true)}

<section><div class="wrap">
  <div class="eyebrow"><span class="no">05</span> By department</div>
  <h2 class="sec">How each function performed</h2>
  <p class="sub">Volume, focus, and the top contributor in each area.</p>
  <div class="grid3">${deptCards}</div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">06</span> Top contributors</div>
  <h2 class="sec">Who carried the work</h2>
  <p class="sub">Ranked by demonstrated on-task output, not just hours logged.</p>
  <div class="card">${topContrib}</div>
</div></section>

<section><div class="wrap">
  <div class="eyebrow"><span class="no">07</span> Automation & leverage</div>
  <h2 class="sec">Where you can reclaim time</h2>
  <p class="sub">Named opportunities, each with an estimated annual recovery.</p>
  <div class="grid2">${autos}</div>
  <div class="asum"><div><div class="k">Total recoverable / year</div><div class="v">${INR(m.totalRecoverable)}</div></div>
    <div><div class="k">Basis</div><div class="v" style="font-size:18px;color:#fff">Reasoned estimate, anchored to tool prevalence</div></div></div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">08</span> Risks — each with a move</div>
  <h2 class="sec">What to watch</h2>
  ${risks}
</div></section>

<section class="dark"><div class="wrap">
  <div class="eyebrow" style="color:var(--gold2)"><span class="no" style="color:#3a4658">09</span> Do this next</div>
  <h2 class="sec" style="color:#fff">Action items</h2>
  <p class="sub" style="color:#9aa6b5">Ranked and specific.</p>
  ${actions}
</div></section>

<section class="foot"><div class="wrap">
  <span class="demo">Illustrative sample · generated by TeamGrid</span><br>
  This is a demonstration report showing the format and depth of insight TeamGrid produces from real workforce activity.
  Figures shown are illustrative. In a live TeamGrid deployment, every metric is derived from real, consented activity data,
  daily hours are capped at 16h, and automation figures are reasoned estimates labelled as recoverable — never measured guarantees.
  No raw screen content is ever shown. &nbsp;·&nbsp; TeamGrid — Workforce Intelligence, Automated.
</div></section>

</body></html>`;
}

// ─── Extra CSS shared by the new templates ───────────────────────────────────
const EXTRA_CSS = `
/* compact cover variant */
.cover.slim{min-height:auto;padding:64px 0 54px}
.cover.slim h1{font-size:46px;max-width:20ch}
/* weekly highlight blocks */
.wk{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.hl{background:#fff;border:1px solid var(--line);border-left:4px solid var(--gold);border-radius:12px;padding:18px 20px}
.hl .t{font-weight:600;font-size:15px;margin-bottom:5px}.hl .d{font-size:13.5px;color:#48525f;font-weight:300}
.mover{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid #f1f3f5}.mover:last-child{border-bottom:0}
.mover .rank{font-family:ui-monospace,Menlo,monospace;font-weight:700;color:var(--gold);width:26px}
.mover .mn{font-weight:600;font-size:14.5px}.mover .md{font-size:12px;color:var(--sub)}
.mover .mh{margin-left:auto;font-weight:700}
/* one-pager KPI tiles */
.op{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin:20px 0}
.op .t{background:#fff;border:1px solid var(--line);border-radius:12px;padding:16px;text-align:center}
.op .t .n{font-size:26px;font-weight:800;letter-spacing:-.02em}.op .t .l{font-size:10.5px;color:#8b97a6;text-transform:uppercase;letter-spacing:.08em;margin-top:4px}
.opact{display:flex;gap:14px;align-items:baseline;padding:9px 0;border-top:1px solid #f1f3f5}
.opact .n{font-family:ui-monospace,Menlo,monospace;font-weight:700;color:var(--gold)}
/* per-person deep-dive cards (practice + worklog) */
.pcard{background:#fff;border:1px solid var(--line);border-radius:16px;padding:24px;margin-bottom:18px;box-shadow:0 1px 2px rgba(15,27,45,.04),0 6px 22px rgba(15,27,45,.04)}
.phead{display:flex;align-items:center;gap:14px;margin-bottom:16px}
.phead .av{width:46px;height:46px;border-radius:50%;flex:0 0 46px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;background:linear-gradient(135deg,var(--gold),#1971c2)}
.phead .pn{font-size:18px;font-weight:700}.phead .pr{font-size:13px;color:var(--sub)}
.phead .pk{margin-left:auto;display:flex;gap:22px;text-align:right}
.phead .pk .n{font-size:20px;font-weight:700}.phead .pk .l{font-size:10.5px;color:#8b97a6;text-transform:uppercase;letter-spacing:.06em}
.pgrid{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.psec .h{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#8b97a6;margin-bottom:10px}
.chip{display:inline-block;font-size:12.5px;font-weight:500;background:#f4f7fb;border:1px solid var(--line);border-radius:20px;padding:4px 12px;margin:0 6px 6px 0}
.tl{display:flex;gap:6px;align-items:flex-end;height:82px;margin-top:4px}
.tl .c{flex:1;height:100%;display:flex;flex-direction:column;align-items:center;gap:5px;justify-content:flex-end}
.tl .c .b{width:100%;max-width:30px;min-height:4px;border-radius:5px 5px 0 0;background:linear-gradient(180deg,var(--gold2),var(--gold))}
.tl .c .d{font-size:9px;color:#a3adb9}
.grpttl{font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--gold);margin:34px 0 16px;padding-bottom:8px;border-bottom:1px solid var(--line)}
@media(max-width:640px){.wk,.pgrid{grid-template-columns:1fr}.op{grid-template-columns:1fr 1fr}.phead .pk{display:none}}
@media print{
  .op{grid-template-columns:repeat(5,1fr)!important}
  .wk,.pgrid{grid-template-columns:1fr 1fr!important}
  .pcard,.hl,.op .t{break-inside:avoid}
  .grpttl{break-after:avoid}
  .tl,.chip{break-inside:avoid}
}`;

// ─── Weekly Report renderer (short, cadence-focused) ─────────────────────────
function renderWeekly(m, logoSvg) {
  const highlights = [
    { t: `${esc(m.workMix[0][0])} led the week`, d: `${m.workMix[0][1]}% of tracked hours went into ${m.workMix[0][0].toLowerCase()} — the team's main engine this week.` },
    { t: `${m.aiUsers} of ${m.kpis.people} used AI`, d: `AI-assisted work is now part of the daily flow across the team.` },
    { t: `${m.kpis.focus}% active focus`, d: `${m.kpis.focus >= 78 ? 'Strong, disciplined week with little fragmentation.' : 'Some room to reclaim focus by cutting context switches.'}` },
    { t: `${esc(titleCase(m.people[0].name))} carried most output`, d: `Top contributor at ${m.people[0].hours} h — keep an eye on concentration.` },
  ];
  const movers = m.people.slice(0, 5).map((p, i) => `
    <div class="mover"><span class="rank">${i + 1}</span>
      <div><div class="mn">${esc(titleCase(p.name))}</div><div class="md">${esc(p.role || p.dept || '')}</div></div>
      <span class="mh">${p.hours} h</span></div>`).join('');
  const flags = m.risks.filter((r) => r.sev !== 'Upside').slice(0, 2).map((r) => `
    <div class="hl" style="border-left-color:${r.col}"><div class="t">${esc(r.t)}</div><div class="d">${esc(r.d)} <b>Move:</b> ${esc(r.move)}</div></div>`).join('');

  const body = `
<section class="cover slim"><div class="wrap">
  ${logoBlock(logoSvg)}
  <h1><span class="g">Weekly</span> Team Report</h1>
  <div class="desc">${esc(m.company)} — a quick read on how the week went and what to watch.</div>
  <div class="period">${esc(m.period)} · ${m.kpis.people} people</div>
  <div class="strip">
    <div class="cell"><div class="k">People</div><div class="v">${m.kpis.people}</div></div>
    <div class="cell"><div class="k">Hours</div><div class="v">${m.kpis.hours.toLocaleString('en-IN')}</div></div>
    <div class="cell"><div class="k">Focus</div><div class="v">${m.kpis.focus}<small>%</small></div></div>
    <div class="cell"><div class="k">Using AI</div><div class="v">${m.aiUsers}<small>/${m.kpis.people}</small></div></div>
  </div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">01</span> This week</div>
  <h2 class="sec">Highlights</h2>
  <div class="wk">${highlights.map((h) => `<div class="hl"><div class="t">${esc(h.t)}</div><div class="d">${esc(h.d)}</div></div>`).join('')}</div>
</div></section>

<section><div class="wrap">
  <div class="eyebrow"><span class="no">02</span> Activity</div>
  <h2 class="sec">Where the week went</h2>
  <div class="card"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Work by type</h4>
    <div class="ribbon">${workRibbonHtml(m)}</div><div class="leg">${workLegHtml(m)}</div></div>
  <div class="card" style="margin-top:20px"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Daily activity</h4>
    <div class="days">${dayBarsHtml(m)}</div></div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">03</span> Top movers</div>
  <h2 class="sec">Who did the most</h2>
  <div class="card">${movers}</div>
</div></section>

${flags ? `<section><div class="wrap">
  <div class="eyebrow"><span class="no">04</span> Watch list</div>
  <h2 class="sec">Worth a look</h2>${flags}
</div></section>` : ''}

${footerBlock(true)}`;
  return docShell(m, 'Weekly Team Report', body, EXTRA_CSS);
}

// ─── Executive One-Pager (single dense page) ─────────────────────────────────
function renderOnePager(m, logoSvg) {
  const tiles = [
    ['People', m.kpis.people], ['Hours', m.kpis.hours.toLocaleString('en-IN')],
    ['Focus', m.kpis.focus + '%'], ['AI users', `${m.aiUsers}/${m.kpis.people}`],
    ['Top-2 share', m.top2Share + '%'],
  ].map(([l, n]) => `<div class="t"><div class="n">${n}</div><div class="l">${l}</div></div>`).join('');
  const acts = m.actions.slice(0, 4).map((a, i) => `
    <div class="opact"><span class="n">${String(i + 1).padStart(2, '0')}</span>
      <div><b>${esc(a.t)}</b> — <span style="color:#48525f">${esc(a.d)}</span></div></div>`).join('');

  const body = `
<section style="padding:44px 0 0"><div class="wrap">
  <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:16px;margin-bottom:20px">
    ${logoBlock(logoSvg).replace('class="logo"', 'class="logo" style="margin-bottom:0"').replace('color:#fff', 'color:#0f1b2d')}
    <div style="text-align:right"><div style="font-size:13px;color:var(--sub)">${esc(m.period)} · ${esc(m.industry)}</div>
      <div style="font-family:ui-monospace,Menlo,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--gold)">Executive Summary</div></div>
  </div>
  <h1 style="font-size:34px;font-weight:800;letter-spacing:-.03em;line-height:1.1">${esc(m.company)}</h1>
  <div class="verdict" style="margin-top:16px"><span class="badge" style="background:${m.verdict.col}">${esc(m.verdict.label)}</span><div class="vt" style="font-size:16px">${esc(m.verdict.text)}</div></div>
  <div class="op">${tiles}</div>
  <div class="grid2" style="gap:20px">
    <div class="card" style="padding:20px"><h4 style="font-size:14px;font-weight:600;margin-bottom:12px">Work by type</h4>
      <div class="ribbon">${workRibbonHtml(m)}</div><div class="leg" style="font-size:11.5px">${workLegHtml(m)}</div></div>
    <div class="card" style="padding:20px"><h4 style="font-size:14px;font-weight:600;margin-bottom:12px">Top 3 actions</h4>${acts}</div>
  </div>
  <div class="asum" style="margin-top:20px;padding:20px 26px"><div><div class="k">Automation upside / year</div><div class="v">${INR(m.totalRecoverable)}</div></div>
    <div><div class="k">Top contributor</div><div class="v" style="font-size:20px;color:#fff">${esc(titleCase(m.people[0].name))} · ${m.people[0].hours}h</div></div></div>
</div></section>
${footerBlock(true)}`;
  return docShell(m, 'Executive One-Pager', body, EXTRA_CSS);
}

// ─── Per-person card (shared by Practice + Work Log) ─────────────────────────
function personCard(p, opts = {}) {
  const tools = p.topTools.slice(0, 6).map(([t]) => `<span class="chip">${esc(t)}</span>`).join('');
  const work = p.topWork.slice(0, 4).map(([k, v]) => `<span class="chip">${esc(k)} · ${v}%</span>`).join('');
  // synthetic per-person daily timeline from firm dayTrend, scaled to this person
  const days = (opts.dayTrend || []).slice(0, 14);
  const ratio = p.hours / Math.max(1, opts.avgHours || p.hours);
  const perDay = days.map((d) => Math.round(d.h * ratio * (d.we ? 0.3 : 1)));
  const maxH = Math.max(...perDay, 1);
  const tl = days.map((d, i) =>
    `<div class="c"><div class="b" style="height:${Math.max(6, Math.round(perDay[i] / maxH * 100))}%"></div><div class="d">${d.wd[0]}</div></div>`
  ).join('');
  return `
  <div class="pcard">
    <div class="phead"><div class="av">${esc(initials(p.name))}</div>
      <div><div class="pn">${esc(titleCase(p.name))}</div><div class="pr">${esc(p.role || p.dept || '')}${p.usesAI ? ' · AI-assisted' : ''}</div></div>
      <div class="pk">
        <div><div class="n">${p.hours}</div><div class="l">Hours</div></div>
        <div><div class="n">${p.focus}%</div><div class="l">Focus</div></div>
        <div><div class="n">${p.days}</div><div class="l">Days</div></div>
      </div></div>
    <div class="pgrid">
      <div class="psec"><div class="h">Work breakdown</div>${work}</div>
      <div class="psec"><div class="h">Tools used</div>${tools || '<span class="chip">—</span>'}</div>
    </div>
    ${opts.showTimeline ? `<div class="psec" style="margin-top:16px"><div class="h">Daily activity</div><div class="tl">${tl}</div></div>` : ''}
    ${(p.late >= 5 || p.weekend >= 4) ? `<div class="fwatch" style="margin-top:14px;color:var(--amber)">⚠ Notable ${p.weekend >= 4 ? p.weekend + ' weekend sessions' : 'late-hours working'} — worth a check-in.</div>` : ''}
  </div>`;
}

// ─── Practice / Monthly Review (per-person deep dive, grouped by dept) ───────
function renderPractice(m, logoSvg) {
  const avgHours = m.kpis.hours / m.kpis.people;
  const groups = m.departments.map((d) => {
    const dp = m.people.filter((p) => p.dept === d.name);
    if (!dp.length) return '';
    const cards = dp.map((p) => personCard(p, { dayTrend: m.dayTrend, avgHours, showTimeline: false })).join('');
    return `<div class="grpttl">${esc(d.name)} · ${d.count} ${d.count === 1 ? 'person' : 'people'} · ${d.hours} h · ${d.focus}% focus</div>${cards}`;
  }).join('');

  const body = `
<section class="cover slim"><div class="wrap">
  ${logoBlock(logoSvg)}
  <h1><span class="g">Monthly Practice</span> Review</h1>
  <div class="desc">${esc(m.company)} — a person-by-person account of how the ${esc(m.period)} was spent.</div>
  <div class="period">${esc(m.industry)} · ${esc(m.period)} · ${m.workingDays} working days</div>
  <div class="strip">
    <div class="cell"><div class="k">People</div><div class="v">${m.kpis.people}</div></div>
    <div class="cell"><div class="k">Hours</div><div class="v">${m.kpis.hours.toLocaleString('en-IN')}</div></div>
    <div class="cell"><div class="k">Focus</div><div class="v">${m.kpis.focus}<small>%</small></div></div>
    <div class="cell"><div class="k">Departments</div><div class="v">${m.departments.length}</div></div>
  </div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">01</span> Firm overview</div>
  <h2 class="sec">The month at a glance</h2>
  <div class="verdict"><span class="badge" style="background:${m.verdict.col}">${esc(m.verdict.label)}</span><div class="vt">${esc(m.verdict.text)}</div></div>
  <div class="card" style="margin-top:22px"><h4 style="font-size:15px;font-weight:600;margin-bottom:14px">Work by type</h4>
    <div class="ribbon">${workRibbonHtml(m)}</div><div class="leg">${workLegHtml(m)}</div></div>
</div></section>

${orgStructureSection(m, '02', false)}

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">03</span> Per-person review</div>
  <h2 class="sec">Everyone's month, in detail</h2>
  <p class="sub">Grouped by department. Hours, focus, work breakdown and tools for each person.</p>
  ${groups}
</div></section>

${footerBlock(false)}`;
  return docShell(m, 'Monthly Practice Review', body, EXTRA_CSS);
}

// ─── Work Log (attendance/activity focused, with per-person timelines) ───────
function renderWorkLog(m, logoSvg) {
  const avgHours = m.kpis.hours / m.kpis.people;
  const totalSessions = m.people.reduce((s, p) => s + p.sessions, 0);
  const cards = m.people.map((p) => personCard(p, { dayTrend: m.dayTrend, avgHours, showTimeline: true })).join('');

  const body = `
<section class="cover slim"><div class="wrap">
  ${logoBlock(logoSvg)}
  <h1><span class="g">Work Log</span> &amp; Activity</h1>
  <div class="desc">${esc(m.company)} — attendance, hours and daily activity for every person.</div>
  <div class="period">${esc(m.period)} · ${m.workingDays} working days</div>
  <div class="strip">
    <div class="cell"><div class="k">People</div><div class="v">${m.kpis.people}</div></div>
    <div class="cell"><div class="k">Total hours</div><div class="v">${m.kpis.hours.toLocaleString('en-IN')}</div></div>
    <div class="cell"><div class="k">Sessions</div><div class="v">${totalSessions.toLocaleString('en-IN')}</div></div>
    <div class="cell"><div class="k">Avg focus</div><div class="v">${m.kpis.focus}<small>%</small></div></div>
  </div>
</div></section>

<section class="alt"><div class="wrap">
  <div class="eyebrow"><span class="no">01</span> Team activity</div>
  <h2 class="sec">Daily activity across the period</h2>
  <div class="card"><div class="days">${dayBarsHtml(m)}</div></div>
</div></section>

<section><div class="wrap">
  <div class="eyebrow"><span class="no">02</span> Per-person log</div>
  <h2 class="sec">Each person's hours & activity</h2>
  <p class="sub">Hours, focus, days present, work breakdown, tools and a daily activity timeline.</p>
  ${cards}
</div></section>

${footerBlock(false)}`;
  return docShell(m, 'Work Log & Activity', body, EXTRA_CSS);
}

// ─── Template registry ───────────────────────────────────────────────────────
export const TEMPLATES = [
  { key: 'ceo-brief', label: 'CEO Brief', desc: 'Insight-dense — every section ends in a decision. The flagship format.', render: renderCEOBrief },
  { key: 'weekly', label: 'Weekly Report', desc: 'Short weekly read — highlights, top movers, quick watch list. 1–2 pages.', render: renderWeekly },
  { key: 'practice', label: 'Monthly Practice Review', desc: 'Person-by-person deep dive grouped by department. Shows depth.', render: renderPractice },
  { key: 'worklog', label: 'Work Log & Activity', desc: 'Attendance & activity focused — per-person hours, days and daily timeline.', render: renderWorkLog },
  { key: 'onepager', label: 'Executive One-Pager', desc: 'Single dense page — verdict, KPIs, work-mix and top actions. A leave-behind.', render: renderOnePager },
];

export function renderReport(templateKey, model, logoSvg) {
  const t = TEMPLATES.find((x) => x.key === templateKey) || TEMPLATES[0];
  return t.render(model, logoSvg);
}
